import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { RootState, useAppDispatch, useAppSelector } from '../../../../store';
import { UserSettings } from '../../../../Types';
import requestClient from '../../../Utility/RequestClient';
import { settingsService } from '../../../../Services/settingsService';
import { Button } from '../../../Utility/Button';
import Select from '../../../Utility/FormElements/Select';
import { SocketContext } from '../../../../SocketContext';
import { Spinner } from '../../../Utility/Spinner';
import * as semver from 'semver';
import { Badge } from '../../../Utility/Badge';
import { CheckIcon, CheckCircleIcon, PencilIcon, WarningCircleIcon } from '@phosphor-icons/react';
import useSettings from '../../../../Hooks/useSettings';
import TextareaAutosize from 'react-textarea-autosize';
import useModal from '../../../Modal/useModal';
import Checkbox from '../../../Utility/FormElements/Checkbox';

// Lokale Versionen zum Testen – auf true setzen, um API zu überspringen
const USE_LOCAL_VERSIONS = true;
const LOCAL_VERSIONS: { stable: string[]; beta: string[]; alpha: string[] } = {
    stable: ['2.0.0', '1.5.2', '1.5.1', '1.5.0', '1.4.0'],
    beta: ['2.1.0-beta.1', '2.0.1-beta.0', '2.0.0'],
    alpha: ['2.2.0-alpha.1', '2.1.0-beta.1', '2.0.0'],
};

function System() {
    const i18n = useAppSelector((state: RootState) => state.i18n.i18n);
    const settings = useAppSelector((state: RootState) => state.settings);
    const userSettings = useAppSelector((state: RootState) => state.userSettings as UserSettings);
    const modal = useModal();
    const { socket } = useContext(SocketContext);
    const [version, setVersion] = useState<string | null>(null);
    const [availableVersions, setAvailableVersions] = useState<string[]>([]);
    const [latestVersion, setLatestVersion] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isUpdating, setIsUpdating] = useState(false);
    const [releaseChannels, setReleaseChannels] = useState<string[]>([]);
    const [latestStableVersion, setLatestStableVersion] = useState<string | null>(null);
    const [licenseKeyDisplay, setLicenseKeyDisplay] = useState<string>('');
    const isSavingRef = useRef(false);
    const latestStableVersionRef = useRef<string | null>(null);
    const { SettingsActions, Currentsettings } = useSettings();

    latestStableVersionRef.current = latestStableVersion;

    const getCanSwitchToStable = useCallback(() => {
        const current = settings.version;
        const latestStable = latestStableVersionRef.current;
        if (!latestStable || !current) return false;
        if (!semver.valid(current) || !semver.valid(latestStable)) return false;
        return semver.lte(current, latestStable);
    }, [settings.version]);

    const openReleaseModal = useCallback(() => {
        const canSwitchToStable = getCanSwitchToStable();

        const content = (
            <div className="grid grid-cols-2 gap-3">
                {releaseChannels.map(
                    (releaseChannel) =>
                        ((userSettings.isAvency && releaseChannel === 'alpha') || releaseChannel !== 'alpha') && (
                            <div className="flex" key={releaseChannel}>
                                <div
                                    className={`relative border-2 rounded-xl p-4 flex-1 flex flex-col ${
                                        releaseChannel === 'stable' &&
                                        settings.releaseChannel !== 'stable' &&
                                        !canSwitchToStable
                                            ? 'border-layout-borderColor opacity-60 cursor-not-allowed'
                                            : settings.releaseChannel === releaseChannel
                                            ? 'border-primary cursor-pointer'
                                            : 'border-layout-borderColor hover:border-primary cursor-pointer'
                                    }`}
                                    onClick={() => {
                                        const isSwitchToStable =
                                            releaseChannel === 'stable' && settings.releaseChannel !== 'stable';
                                        if (isSwitchToStable && !getCanSwitchToStable()) return;
                                        SettingsActions.saveReleaseChannel(releaseChannel);
                                        modal.close();
                                    }}
                                >
                                    {settings.releaseChannel === releaseChannel && (
                                        <div className="h-5 w-5 p-1 rounded-full bg-primary absolute text-layout-textColorOnPrimary top-4 right-4 flex items-center justify-center">
                                            <CheckIcon size={16} />
                                        </div>
                                    )}
                                    <h6 className="mb-3">
                                        {i18n.translate({
                                            id: 'releaseChannel.' + releaseChannel + '.headline',
                                            sourceName: 'System',
                                        })}
                                    </h6>
                                    <div>
                                        {i18n.translate({
                                            id: 'releaseChannel.' + releaseChannel + '.info',
                                            sourceName: 'System',
                                        })}
                                    </div>
                                    {releaseChannel === 'stable' &&
                                        settings.releaseChannel !== 'stable' &&
                                        !canSwitchToStable && (
                                            <div className="mt-2 text-warning flex items-center gap-1">
                                                {i18n.translate({
                                                    id: 'releaseChannel.stable.versionTooNew',
                                                    sourceName: 'System',
                                                })}
                                            </div>
                                        )}
                                </div>
                            </div>
                        ),
                )}
            </div>
        );
        modal.open({
            content: content,
            title: i18n.translate({
                id: 'info.release',
                sourceName: 'System',
            }),
            showCloseButton: true,
            size: 'medium',
        });
    }, [
        releaseChannels,
        modal,
        i18n,
        settings.releaseChannel,
        getCanSwitchToStable,
        SettingsActions,
        userSettings.isAvency,
    ]);

    useEffect(() => {
        const fetchVersions = async () => {
            try {
                const timestamp = new Date().getTime();
                const response = await requestClient.get<{ stable: string[]; beta: string[]; alpha: string[] }>(
                    `https://versions.varios-ai.com/versions.json?timestamp=${timestamp}`,
                );

                const allVersions = [
                    ...(response?.data[settings.releaseChannel as 'stable' | 'beta' | 'alpha'] ?? []),
                    settings.version,
                ]
                    .filter((v, i, arr) => semver.valid(v) && arr.indexOf(v) === i)
                    .sort((a, b) => semver.rcompare(a, b));
                const newestVersion = allVersions[0] ?? null;
                setLatestVersion(newestVersion);

                setAvailableVersions(allVersions);
                setReleaseChannels(Object.keys(response.data));
                setVersion(newestVersion ?? settings.version);

                const stableVersions = (response?.data?.stable ?? []).filter((v) => semver.valid(v));
                const latestStable =
                    stableVersions.length > 0 ? stableVersions.sort((a, b) => semver.rcompare(a, b))[0] : null;
                setLatestStableVersion(latestStable);
            } catch (error) {
                console.error('Error fetching available versions:', error);
            } finally {
                setIsLoading(false);
            }
        };

        const timer = setTimeout(fetchVersions, 1000);
        return () => clearTimeout(timer);
    }, [settings.version, requestClient, settings.releaseChannel]);

    const handleUpdateVersion = async () => {
        if (!version || !socket) return;

        try {
            setIsUpdating(true);
            const params = {
                type: 'systemStatus',
                systemStatus: 'updating',
            };

            socket.send(JSON.stringify(params));
            await settingsService.updateVersion(version);
        } catch (error) {
            console.error('Error updating version:', error);
        } finally {
            setIsUpdating(false);
        }
    };

    const isLatestVersion = settings.version === latestVersion;

    useEffect(() => {
        if (isSavingRef.current && licenseKeyDisplay) {
            const timer = setTimeout(() => {
                setLicenseKeyDisplay('');
                isSavingRef.current = false;
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [settings, licenseKeyDisplay]);

    return (
        <div className="w-full flex gap-5 flex-col">
            <h4>{i18n.translate({ id: 'title', sourceName: 'System' })}</h4>

            <div className="flex flex-col gap-3">
                <div className="flex gap-2">
                    <span className="font-semibold">
                        {i18n.translate({ id: 'info.status', sourceName: 'System' })}:
                    </span>
                    <span>{settings.systemStatus}</span>
                </div>

                <div className="flex gap-2">
                    <span className="font-semibold">
                        {i18n.translate({ id: 'info.version', sourceName: 'System' })}:
                    </span>
                    <span>{settings.version}</span>
                </div>

                <div className="flex gap-2 items-center">
                    <span className="font-semibold">
                        {i18n.translate({ id: 'info.release', sourceName: 'System' })}:
                    </span>
                    <span>
                        {i18n.translate({
                            id: 'releaseChannel.' + settings.releaseChannel + '.headline',
                            sourceName: 'System',
                        })}
                    </span>
                    {!isLoading && (
                        <div>
                            <PencilIcon size={16} onClick={openReleaseModal} />
                        </div>
                    )}
                </div>

                {settings.accountId && (
                    <div className="flex gap-2">
                        <span className="font-semibold">
                            {i18n.translate({ id: 'info.accountid', sourceName: 'System' })}:
                        </span>
                        <span>{settings.accountId}</span>
                    </div>
                )}

                <div className="flex gap-2 items-center">
                    <label className="flex items-center space-x-3 cursor-pointer opacity-100">
                        <div className="relative h-6 w-6">
                            <Checkbox
                                checked={Currentsettings.supportLoginEnabled}
                                onChange={() =>
                                    SettingsActions.updateCurrentSettings({
                                        supportLoginEnabled: !Currentsettings.supportLoginEnabled,
                                    })
                                }
                            />
                        </div>
                        <span>{i18n.translate({ id: 'supportLoginEnabled', sourceName: 'System' })}</span>
                    </label>
                </div>
            </div>

            <label>
                <strong>{i18n.translate({ id: 'licenseKey', sourceName: 'Settings' })}</strong>
                <TextareaAutosize
                    className="bg-input-backgroundColor w-full mt-2 p-3 border border-2 border-input-borderColor rounded-lg outline-none focus:border-primary transition-all duration-100"
                    value={licenseKeyDisplay}
                    onChange={(value) => {
                        const newValue = value.target.value;
                        setLicenseKeyDisplay(newValue);
                    }}
                    placeholder={licenseKeyDisplay === '' ? '••••••••' : ''}
                />
            </label>

            <div className="">
                <Button
                    onClick={() => {
                        if (licenseKeyDisplay) {
                            isSavingRef.current = true;
                        }
                        SettingsActions.handleUpdateSettings(licenseKeyDisplay || undefined);
                    }}
                >
                    {i18n.translate({ id: 'save', sourceName: 'Main' })}
                </Button>
            </div>

            {userSettings.isSuperAdmin && settings.selfUpdateActive && (
                <>
                    <h4>{i18n.translate({ id: 'update.title', sourceName: 'System' })}</h4>

                    <div className="flex flex-col gap-4">
                        <div className="flex items-center gap-3">
                            {isLoading ? (
                                <Spinner
                                    size="lg"
                                    text={i18n.translate({ id: 'update.checking', sourceName: 'System' })}
                                />
                            ) : (
                                <>
                                    <Badge variant={isLatestVersion ? 'success' : 'warning'} className="w-fit">
                                        <div className="flex gap-1 items-center">
                                            {isLatestVersion ? (
                                                <CheckCircleIcon size={16} />
                                            ) : (
                                                <WarningCircleIcon size={16} />
                                            )}
                                            {isLatestVersion
                                                ? i18n.translate({ id: 'update.latest', sourceName: 'System' })
                                                : i18n.translate({ id: 'update.available', sourceName: 'System' })}
                                        </div>
                                    </Badge>
                                </>
                            )}
                        </div>

                        {!isLoading && availableVersions.length > 0 && (
                            <div className="flex gap-3 items-center">
                                <Select
                                    value={version}
                                    onChange={(version) => setVersion(version)}
                                    classList="max-w-[240px]"
                                >
                                    {availableVersions.map((availableVersion) => (
                                        <option key={availableVersion} value={availableVersion}>
                                            {availableVersion}
                                            {availableVersion === settings.version &&
                                                ` (${i18n.translate({
                                                    id: 'update.currentVersion',
                                                    sourceName: 'System',
                                                })})`}
                                        </option>
                                    ))}
                                </Select>
                                {version !== settings.version && (
                                    <Button
                                        onClick={() =>
                                            modal.confirm({
                                                title: i18n.translate({
                                                    id: 'update.confirm.title',
                                                    sourceName: 'System',
                                                }),
                                                content: i18n.translate({
                                                    id: 'update.confirm.text',
                                                    sourceName: 'System',
                                                }),

                                                onConfirm: handleUpdateVersion,
                                                confirmText: i18n.translate({
                                                    id: 'update.confirm.button',
                                                    sourceName: 'System',
                                                }),
                                                cancelText: i18n.translate({
                                                    id: 'update.cancel.button',
                                                    sourceName: 'System',
                                                }),
                                            })
                                        }
                                        className={isLatestVersion ? 'opacity-80' : ''}
                                        disabled={isUpdating}
                                    >
                                        {isUpdating
                                            ? i18n.translate({ id: 'update.updating', sourceName: 'System' })
                                            : isLatestVersion
                                            ? i18n.translate({ id: 'update.downgrade', sourceName: 'System' })
                                            : i18n.translate({ id: 'update.button', sourceName: 'System' })}
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

export default System;
